import { useEffect, useRef } from 'react'

interface ScrollDepthTrackerProps {
  containerElement?: React.RefObject<HTMLElement>
  scrollContentElement: React.RefObject<HTMLElement>
  options?: {
    throttleMs?: number
  }
  checkpoints?: number[]
  onTrigger?: (checkpoint: number) => void
}

/**
 * 스크롤 퍼센트에 따라 체크포인트에 도달했을 때 이벤트를 발생시키는 훅
 *
 * @param containerElement 스크롤 컨테이너 요소
 * @param scrollContentElement 스크롤 컨텐츠 요소
 * @param checkpoints 트리거할 스크롤 퍼센트 체크포인트 배열 (0-100 사이의 숫자)
 * @param onTrigger 체크포인트 도달 시 호출될 콜백 함수
 * @param options 추가 설정 옵션
 * @returns 이미 트리거된 체크포인트 배열
 */
export const useScrollDepthTracker = ({
  containerElement,
  scrollContentElement,
  options,
  onTrigger,
  checkpoints = [10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 100],
}: ScrollDepthTrackerProps) => {
  const triggeredScrollPercentagesRef = useRef(new Set<number>())
  useEffect(() => {
    if (!scrollContentElement.current) return

    let isEventBlocked = false
    let timeout: NodeJS.Timeout | null = null
    const trackScrollDepth = (currentPercentage: number) => {
      const missedCheckpoints = checkpoints.filter(
        (checkpoint) => currentPercentage >= checkpoint && !triggeredScrollPercentagesRef.current.has(checkpoint),
      )

      for (const checkpoint of missedCheckpoints) {
        triggeredScrollPercentagesRef.current.add(checkpoint)
        onTrigger?.(checkpoint)
      }
    }

    const handleScroll = (event: Event) => {
      const isUserScroll = !!event
      if (!isUserScroll) return
      if (isEventBlocked) return
      if (!scrollContentElement.current) return
      try {
        const currentPercentage = calculateScrollPercentage({
          element: scrollContentElement.current,
          containerElement: containerElement?.current,
        })
        trackScrollDepth(currentPercentage)

        isEventBlocked = true

        timeout = setTimeout(() => {
          if (!scrollContentElement.current) return
          try {
            isEventBlocked = false
            const finalPercentage = calculateScrollPercentage({
              element: scrollContentElement.current,
              containerElement: containerElement?.current,
            })
            trackScrollDepth(finalPercentage)
          } catch (error) {
            console.error('[ScrollTracker] Error:', error)
          }
        }, options?.throttleMs ?? 200)
      } catch (error) {
        console.error('[ScrollTracker] Error:', error)
      }
    }

    ;(containerElement?.current || window).addEventListener('scroll', handleScroll)
    return () => {
      ;(containerElement?.current || window).removeEventListener('scroll', handleScroll)
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [scrollContentElement, containerElement, onTrigger, checkpoints, options])
}

const calculateScrollPercentage = ({
  element,
  containerElement,
}: {
  element: HTMLElement
  containerElement?: HTMLElement | null
}) => {
  if (!element) return 0

  const windowHeight = containerElement ? containerElement.clientHeight : window.innerHeight
  const documentHeight = element.scrollHeight
  const scrollTop = containerElement ? containerElement.scrollTop : window.scrollY

  if (scrollTop + windowHeight >= documentHeight) {
    return 100
  }

  return Math.floor((scrollTop / (documentHeight - windowHeight)) * 100)
}
